import { LazyLoader } from "@NextSkyTheme/lazy-load";
import * as NextSkyTheme from "@NextSkyTheme/global";
import { CustomElement } from "@NextSkyTheme/safari-element-patch";
import { eventModal } from "@NextSkyTheme/modal";

let cachedPriceInputFormatConfig = null;
let cachedPriceInputFormatString = null;

function extractFormattedNumericValue(value) {
  return String(value || "")
    .replace(/[^\d,.' ]/g, "")
    .trim()
    .replace(/\s{2,}/g, " ");
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function getPriceInputFormatConfig() {
  const moneyFormat = String(window.cartStrings?.money_format || "{{ amount }}");

  if (
    cachedPriceInputFormatConfig &&
    cachedPriceInputFormatString === moneyFormat
  ) {
    return cachedPriceInputFormatConfig;
  }

  const baseSample = extractFormattedNumericValue(
    NextSkyTheme.formatMoney(100, moneyFormat)
  );
  const largeSample = extractFormattedNumericValue(
    NextSkyTheme.formatMoney(123456789, moneyFormat)
  );

  let precision = 0;
  let decimal = ".";
  let thousands = ",";

  const decimalMatch = baseSample.match(/([,.' ])(\d+)$/);
  if (decimalMatch) {
    decimal = decimalMatch[1];
    precision = decimalMatch[2].length;
  }

  let integerSample = largeSample;
  if (precision > 0) {
    integerSample = largeSample.replace(
      new RegExp(`${escapeRegExp(decimal)}\\d{${precision}}$`),
      ""
    );
  }

  const thousandsMatch = integerSample.match(/[,.' ]/);
  if (thousandsMatch) {
    thousands = thousandsMatch[0];
  }

  cachedPriceInputFormatString = moneyFormat;
  cachedPriceInputFormatConfig = {
    precision,
    thousands,
    decimal,
  };

  return cachedPriceInputFormatConfig;
}

function parsePriceInputValue(value) {
  const { precision, thousands, decimal } = getPriceInputFormatConfig();
  const sanitizedValue = String(value || "")
    .trim()
    .replace(/[\s']/g, "")
    .replace(/[^\d,.]/g, "");

  if (!sanitizedValue) {
    return {
      displayValue: "",
      normalizedValue: "",
    };
  }

  let decimalSeparator = "";
  if (sanitizedValue.includes(",") && sanitizedValue.includes(".")) {
    decimalSeparator =
      sanitizedValue.lastIndexOf(",") > sanitizedValue.lastIndexOf(".")
        ? ","
        : ".";
  } else if (precision > 0 && sanitizedValue.includes(decimal)) {
    decimalSeparator = decimal;
  }

  let integerPart = sanitizedValue;
  let fractionPart = "";
  let hasTrailingDecimal = false;

  if (decimalSeparator) {
    const separatorIndex = sanitizedValue.lastIndexOf(decimalSeparator);
    integerPart = sanitizedValue.slice(0, separatorIndex);
    fractionPart = sanitizedValue.slice(separatorIndex + 1).replace(/\D/g, "");
    hasTrailingDecimal = sanitizedValue.endsWith(decimalSeparator);
  }

  integerPart = integerPart.replace(/\D/g, "").replace(/^0+(?=\d)/, "");

  if (integerPart === "") {
    integerPart = "0";
  }

  if (precision === 0) {
    fractionPart = "";
    hasTrailingDecimal = false;
  } else if (fractionPart.length > precision) {
    fractionPart = fractionPart.slice(0, precision);
  }

  const groupedIntegerPart = integerPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    thousands
  );

  let displayValue = groupedIntegerPart;
  if (hasTrailingDecimal) {
    displayValue = `${groupedIntegerPart}${decimal}`;
  } else if (fractionPart !== "") {
    displayValue = `${groupedIntegerPart}${decimal}${fractionPart}`;
  }

  let normalizedValue = integerPart;
  if (fractionPart !== "") {
    normalizedValue = `${integerPart}.${fractionPart}`;
  }

  return {
    displayValue,
    normalizedValue,
  };
}

function formatNormalizedPriceInputValue(value) {
  if (value == null || value === "") return "";

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "";

  return extractFormattedNumericValue(
    NextSkyTheme.formatMoney(
      Math.round(numericValue * 100),
      window.cartStrings?.money_format
    )
  );
}

function normalizePriceInputValue(value) {
  return parsePriceInputValue(value).normalizedValue;
}

function getPriceRangeInputs(root) {
  if (!root) return {};

  return {
    minDisplayInput: root.querySelector('[data-price-input="min-display"]'),
    maxDisplayInput: root.querySelector('[data-price-input="max-display"]'),
    minValueInput: root.querySelector('[data-price-input="min-value"]'),
    maxValueInput: root.querySelector('[data-price-input="max-value"]'),
  };
}

function setPriceInputValue(displayInput, hiddenInput, value, formatValue = false) {
  if (!displayInput || !hiddenInput) return;

  const normalizedValue = normalizePriceInputValue(value);
  hiddenInput.value = normalizedValue;
  displayInput.value =
    formatValue && normalizedValue !== ""
      ? formatNormalizedPriceInputValue(normalizedValue)
      : normalizedValue;
}

function getProductsGridContainer() {
  return document.getElementById("ProductsGridContainer");
}

function getCollectionGrid(doc = document) {
  return doc.getElementById("CollectionGird");
}

function getProductGrid(doc = document) {
  return doc.getElementById("product-grid");
}

function setCollectionLoadingState(isLoading) {
  getProductsGridContainer()?.classList.toggle("loading", isLoading);
  NextSkyTheme.getBody().classList.toggle("loading", isLoading);
}

function scrollToProductsGrid() {
  const scrollIntoView = getProductsGridContainer();
  if (!scrollIntoView) return;

  let offsetHeight = 0;
  const collectionSteps = scrollIntoView.querySelector(".collection-steps");
  if (collectionSteps) {
    offsetHeight = collectionSteps.offsetHeight + 150;
  }

  window.scrollTo({
    top:
      scrollIntoView.getBoundingClientRect().top +
      window.scrollY -
      110 +
      offsetHeight,
    behavior: "smooth",
  });
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.debouncedOnSubmit = NextSkyTheme.debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);
    this.boundDebouncedOnSubmit = this.debouncedOnSubmit.bind(this);

    this.facetForm = this.querySelector("form");
    if (this.facetForm) {
      this.facetForm.addEventListener("input", this.boundDebouncedOnSubmit);
    }
  }

  disconnectedCallback() {
    if (this.facetForm) {
      this.facetForm.removeEventListener("input", this.boundDebouncedOnSubmit);
    }
  }

  static setListeners() {
    if (FacetFiltersForm.listenersSet) return;
    FacetFiltersForm.onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", FacetFiltersForm.onHistoryChange);
    FacetFiltersForm.listenersSet = true;
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const requestId = ++FacetFiltersForm.renderRequestId;
    const sections = FacetFiltersForm.getSections();
    if (sections.length === 0) {
      FacetFiltersForm.clearLoadingState();
      return;
    }
    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;
      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event, requestId);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event, requestId) {
    FacetFiltersForm.abortRenderFetch();
    const controller = new AbortController();
    FacetFiltersForm.renderAbortController = controller;
    setCollectionLoadingState(true);
    scrollToProductsGrid();
    fetch(url, { signal: controller.signal })
      .then((response) => response.text())
      .then((responseText) => {
        if (
          requestId !== FacetFiltersForm.renderRequestId ||
          controller.signal.aborted
        ) {
          return;
        }
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        const htmlRender = new DOMParser().parseFromString(html, "text/html");
        FacetFiltersForm.renderFilters(htmlRender, event);
        FacetFiltersForm.renderProductContainer(htmlRender);
        FacetFiltersForm.renderProductCount(htmlRender);
        new LazyLoader(".image-lazy-load");
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        console.error(error);
        if (requestId === FacetFiltersForm.renderRequestId) {
          FacetFiltersForm.clearLoadingState();
        }
      })
      .finally(() => {
        if (FacetFiltersForm.renderAbortController === controller) {
          FacetFiltersForm.renderAbortController = null;
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    FacetFiltersForm.abortRenderFetch();
    setCollectionLoadingState(true);
    scrollToProductsGrid();
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    const htmlRender = new DOMParser().parseFromString(html, "text/html");
    FacetFiltersForm.renderFilters(htmlRender, event);
    FacetFiltersForm.renderProductContainer(htmlRender);
    FacetFiltersForm.renderProductCount(htmlRender);
    new LazyLoader(".image-lazy-load");
  }

  static abortRenderFetch() {
    if (!FacetFiltersForm.renderAbortController) return;
    FacetFiltersForm.renderAbortController.abort();
    FacetFiltersForm.renderAbortController = null;
  }

  static clearLoadingState() {
    setCollectionLoadingState(false);
  }

  static renderProductContainer(htmlRender) {
    const destination = getCollectionGrid();
    const source = getCollectionGrid(htmlRender);
    if (!destination || !source) return;

    destination.innerHTML = source.innerHTML;
  }

  static renderProductCount(htmlRender) {
    const updateContent = (blockClass) => {
      if (blockClass == "facet-drawer") {
        if (
          window.innerWidth < 1025 ||
          !document.querySelector("#FacetFiltersForm")
        ) {
          return;
        }
      }
      const source = htmlRender.querySelector(`.${blockClass}`);
      const destination = document.querySelector(`.${blockClass}`);
      if (source && destination) {
        destination.innerHTML = source.innerHTML;
      }
    };

    const blocksToUpdate = [
      "collection-count",
      "facets-filters-active",
      "facets-filters-drawer-active",
      "select-sort.facet-filters",
      "drawer-sort.facet-filters",
      "pagination-load-more",
      "facet-drawer",
    ];
    blocksToUpdate.forEach(updateContent);
  }

  static renderFilters(htmlRender, event) {
    const facetDetailsElements = htmlRender.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormDrawer .js-filter"
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element)
    );

    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);
    facetsToRender.forEach((element) => {
      if (
        document.querySelector(
          `.js-filter[data-index="${element.dataset.index}"]`
        )
      ) {
        document.querySelector(
          `.js-filter[data-index="${element.dataset.index}"]`
        ).innerHTML = element.innerHTML;
      }
    });

    if (
      htmlRender.querySelector(".js-filter-apply") &&
      document.querySelector(".js-filter-apply")
    ) {
      document.querySelector(".js-filter-apply").innerHTML =
        htmlRender.querySelector(".js-filter-apply").innerHTML;
    }

    if (
      htmlRender.querySelector(".js-filter-facet") &&
      document.querySelector(".js-filter-facet")
    ) {
      document.querySelector(".js-filter-facet").innerHTML =
        htmlRender.querySelector(".js-filter-facet").innerHTML;
    }

    FacetFiltersForm.renderActiveFacets(htmlRender);

    if (countsToRender)
      FacetFiltersForm.renderCounts(
        countsToRender,
        event.target.closest(".js-filter")
      );
    FacetFiltersForm.clearLoadingState();
  }

  static renderActiveFacets(html) {
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector(".facets__selected");
    const sourceElement = source.querySelector(".facets__selected");

    const targetElementAccessibility = target.querySelector(".facets__summary");
    const sourceElementAccessibility = source.querySelector(".facets__summary");

    if (sourceElement && targetElement) {
      target.querySelector(".facets__selected").outerHTML =
        source.querySelector(".facets__selected").outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector(".facets__summary").outerHTML =
        source.querySelector(".facets__summary").outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    );
  }

  static getSections() {
    const productGrid = getProductGrid();
    const sectionId = productGrid?.dataset?.id;
    if (!sectionId) return [];

    return [
      {
        section: sectionId,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const filteredFormData = new FormData();
    for (let [key, value] of formData.entries()) {
      filteredFormData.append(key, value);
    }
    return new URLSearchParams(filteredFormData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    if (event.target.matches('[data-price-input$="-display"]')) {
      return;
    }
    if (event.target.classList.contains("max-price")) {
      const priceRangeElement = event.target.closest("price-range");
      const { minValueInput, maxValueInput } = getPriceRangeInputs(
        priceRangeElement
      );
      const minValue = Number(minValueInput?.value || 0);
      const maxValue = Number(maxValueInput?.value || 0);
      const rangeMaxValue = Number(
        event.target.dataset.maxValue || priceRangeElement?.dataset.rangeMax || 0
      );
      if (maxValue <= minValue || maxValue > rangeMaxValue) {
        return;
      }
    }
    const sortFilterForms = document.querySelectorAll(
      "facet-filters-form form"
    );
    const targetForm = event.target.closest("form");
    const forms = [];
    const isFiltersForm = targetForm.id === "FacetFiltersForm";
    sortFilterForms.forEach((form) => {
      if (isFiltersForm) {
        if (form.id === "FacetSortForm" || form.id === "FacetFiltersForm") {
          forms.push(this.createSearchParams(form));
        }
      } else {
        if (
          form.id === "FacetSortForm" ||
          form.id === "FacetFiltersFormDrawer"
        ) {
          forms.push(this.createSearchParams(form));
        }
      }
    });
    this.onSubmitForm(forms.join("&"), event);
  }

  facetApplyFilter(event) {
    event.preventDefault();
    const drawer = document.querySelector("facet-drawer");
    const forms = [];
    const sortFilterForms = document.querySelectorAll(
      "facet-filters-form form"
    );
    sortFilterForms.forEach((form) => {
      if (form.id === "FacetSortForm" || form.id === "FacetFiltersFormDrawer") {
        forms.push(this.createSearchParams(form));
      }
    });
    this.onSubmitForm(forms.join("&"), event);
    eventModal(drawer, "close");
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1
          );
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.renderAbortController = null;
FacetFiltersForm.renderRequestId = 0;
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRangeDrag extends HTMLElement {
  constructor() {
    super();
    this.inputCleanupHandlers = [];
    this.adjustToValidValues();
  }

  disconnectedCallback() {
    this.cleanupInputHandlers();
  }

  addManagedInputListener(target, type, handler, options) {
    if (!target) return;
    target.addEventListener(type, handler, options);
    this.inputCleanupHandlers.push(() =>
      target.removeEventListener(type, handler, options)
    );
  }

  cleanupInputHandlers() {
    this.inputCleanupHandlers.forEach((cleanup) => cleanup());
    this.inputCleanupHandlers = [];
  }

  countPercentMin(value) {
    const rangemax = this.dataset?.rangeMax;
    if (!rangemax || rangemax === 0) return;
    let from = 0;
    if (value || rangemax !== 0) {
      from = (value / rangemax) * 100;
    }
    this.style.setProperty("--range-from", from + "%");
    if (Number(from) > Number(this.getValue("--range-to", this))) {
      this.style.setProperty("--range-to", from + "%");
    }
  }
  countPercentMax(value) {
    const rangemax = this.dataset?.rangeMax;
    if (!rangemax || rangemax === 0) return;
    let to = 0;
    if (value || rangemax !== 0) {
      to = (value / rangemax) * 100;
    }
    this.style.setProperty("--range-to", to + "%");
    if (Number(this.getValue("--range-from", this)) > Number(to)) {
      this.style.setProperty("--range-from", to + "%");
    }
  }
  getValue(value, priceDrag) {
    var computedStyle = getComputedStyle(priceDrag);
    var fromValue = computedStyle.getPropertyValue(value);
    return fromValue.replace("%", "");
  }

  adjustToValidValues() {
    this.cleanupInputHandlers();
    const _this = this;
    var inputRange = this.querySelectorAll("input");
    const priceRange = this.closest("action-filter")?.querySelector("price-range");
    const { minDisplayInput, maxDisplayInput, minValueInput, maxValueInput } =
      getPriceRangeInputs(priceRange);
    var rangeInput = inputRange[0];
    var rangeInput2 = inputRange[1];
    if (
      !rangeInput ||
      !rangeInput2 ||
      !minDisplayInput ||
      !maxDisplayInput ||
      !minValueInput ||
      !maxValueInput
    ) {
      return;
    }

    this.addManagedInputListener(rangeInput, "input", function () {
      let nextMinValue = rangeInput.value;
      let nextMaxValue = rangeInput2.value;

      if (Number(nextMinValue) > Number(nextMaxValue)) {
        nextMaxValue = nextMinValue;
        rangeInput2.value = nextMinValue;
      }

      setPriceInputValue(minDisplayInput, minValueInput, nextMinValue, true);
      setPriceInputValue(maxDisplayInput, maxValueInput, nextMaxValue, true);
      _this.countPercentMin(Number(nextMinValue));
    });

    this.addManagedInputListener(rangeInput2, "input", function () {
      let nextMinValue = rangeInput.value;
      let nextMaxValue = rangeInput2.value;

      if (Number(nextMaxValue) < Number(nextMinValue)) {
        nextMinValue = nextMaxValue;
        rangeInput.value = nextMaxValue;
      }

      setPriceInputValue(minDisplayInput, minValueInput, nextMinValue, true);
      setPriceInputValue(maxDisplayInput, maxValueInput, nextMaxValue, true);
      _this.countPercentMax(Number(nextMaxValue));
    });
  }
}
customElements.define("price-range-drag", PriceRangeDrag);

class PriceRange extends PriceRangeDrag {
  constructor() {
    super();
    this.priceDrag =
      this.closest("action-filter")?.querySelector("price-range-drag");
    this.focusFrame = 0;
    this.boundHandleFocusout = null;
  }

  disconnectedCallback() {
    if (typeof super.disconnectedCallback === "function") {
      super.disconnectedCallback();
    }
    if (this.boundHandleFocusout) {
      this.removeEventListener("focusout", this.boundHandleFocusout);
      this.boundHandleFocusout = null;
    }
    if (this.focusFrame) {
      cancelAnimationFrame(this.focusFrame);
      this.focusFrame = 0;
    }
  }
  countPercentMin(value) {
    if (!this.priceDrag) return;
    const rangemax = this.priceDrag.dataset?.rangeMax;
    if (!rangemax || rangemax === 0) return;
    let from = 0;
    if (value || rangemax !== 0) {
      from = (value / rangemax) * 100;
    }
    this.priceDrag.style.setProperty("--range-from", from + "%");
    if (Number(from) > Number(this.getValue("--range-to", this.priceDrag))) {
      this.priceDrag.style.setProperty("--range-to", from + "%");
    }
  }
  countPercentMax(value) {
    if (!this.priceDrag) return;
    const rangemax = this.priceDrag.dataset?.rangeMax;
    if (!rangemax || rangemax === 0) return;
    let to = 0;
    if (value || rangemax !== 0) {
      to = (value / rangemax) * 100;
    }
    this.priceDrag.style.setProperty("--range-to", to + "%");
    if (Number(this.getValue("--range-from", this.priceDrag)) > Number(to)) {
      this.priceDrag.style.setProperty("--range-from", to + "%");
    }
  }
  adjustToValidValues() {
    this.cleanupInputHandlers();
    const _this = this;
    const {
      minDisplayInput,
      maxDisplayInput,
      minValueInput,
      maxValueInput,
    } = getPriceRangeInputs(this);
    var inputRange = this.closest("action-filter")?.querySelectorAll(
      "price-range-drag input"
    );
    var rangeInput = inputRange[0];
    var rangeInput2 = inputRange[1];
    if (
      !rangeInput ||
      !rangeInput2 ||
      !minDisplayInput ||
      !maxDisplayInput ||
      !minValueInput ||
      !maxValueInput
    ) {
      return;
    }

    this.addManagedInputListener(minDisplayInput, "keydown", function (event) {
      if (event.key === "e" || event.key === "+" || event.key === "-")
        event.preventDefault();
    });
    this.addManagedInputListener(minDisplayInput, "input", function () {
      const parsedMinValue = parsePriceInputValue(minDisplayInput.value);

      minDisplayInput.value = parsedMinValue.displayValue;
      minValueInput.value = parsedMinValue.normalizedValue;

      if (parsedMinValue.normalizedValue !== "") {
        const nextMinValue = Math.min(
          Number(parsedMinValue.normalizedValue),
          Number(rangeInput.max)
        );
        rangeInput.value = nextMinValue;
        _this.countPercentMin(nextMinValue);
      }
    });
    this.addManagedInputListener(maxDisplayInput, "keydown", function (event) {
      if (event.key === "e" || event.key === "+" || event.key === "-")
        event.preventDefault();
    });
    this.addManagedInputListener(maxDisplayInput, "input", function () {
      const parsedMaxValue = parsePriceInputValue(maxDisplayInput.value);

      maxDisplayInput.value = parsedMaxValue.displayValue;
      maxValueInput.value = parsedMaxValue.normalizedValue;

      if (parsedMaxValue.normalizedValue !== "") {
        const nextMaxValue = Math.min(
          Number(parsedMaxValue.normalizedValue),
          Number(rangeInput2.max)
        );
        rangeInput2.value = nextMaxValue;
        _this.countPercentMax(nextMaxValue);
      }
    });

    const commitPriceInputs = () => {
      const minAllowed = Number(minDisplayInput.dataset.minValue || 0);
      const maxAllowed = Number(maxDisplayInput.dataset.maxValue || 0);

      let committedMinValue = normalizePriceInputValue(minDisplayInput.value);
      if (committedMinValue === "" || Number(committedMinValue) < minAllowed) {
        committedMinValue = String(minAllowed);
      }
      if (Number(committedMinValue) > maxAllowed) {
        committedMinValue = String(maxAllowed);
      }

      let committedMaxValue = normalizePriceInputValue(maxDisplayInput.value);
      if (committedMaxValue === "" || Number(committedMaxValue) > maxAllowed) {
        committedMaxValue = String(maxAllowed);
      }
      if (Number(committedMaxValue) <= Number(committedMinValue)) {
        committedMaxValue = String(Number(committedMinValue) + 1);
      }
      if (Number(committedMaxValue) > maxAllowed) {
        committedMaxValue = String(maxAllowed);
      }

      setPriceInputValue(
        minDisplayInput,
        minValueInput,
        committedMinValue,
        true
      );
      setPriceInputValue(
        maxDisplayInput,
        maxValueInput,
        committedMaxValue,
        true
      );

      if (minValueInput.value !== "") {
        rangeInput.value = Number(minValueInput.value);
        _this.countPercentMin(Number(minValueInput.value));
      }
      if (maxValueInput.value !== "") {
        rangeInput2.value = Number(maxValueInput.value);
        _this.countPercentMax(Number(maxValueInput.value));
      }
    };

    if (this.boundHandleFocusout) {
      this.removeEventListener("focusout", this.boundHandleFocusout);
    }
    this.boundHandleFocusout = () => {
      if (this.focusFrame) cancelAnimationFrame(this.focusFrame);
      this.focusFrame = requestAnimationFrame(() => {
        this.focusFrame = 0;
        if (!this.isConnected) return;
        const activeElement = document.activeElement;
        const priceRangeDrag = this.closest("action-filter")?.querySelector(
          "price-range-drag"
        );

        if (
          this.contains(activeElement) ||
          priceRangeDrag?.contains(activeElement)
        ) {
          return;
        }

        commitPriceInputs();
        maxValueInput.dispatchEvent(new Event("input", { bubbles: true }));
      });
    };
    this.addEventListener("focusout", this.boundHandleFocusout);
  }
}
customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.facetLink = this.querySelector("a");
    this.boundCloseFilter = this.closeFilter.bind(this);
    this.boundHandleKeyup = this.handleKeyup.bind(this);
    if (!this.facetLink) return;
    this.facetLink.setAttribute("role", "button");
    this.facetLink.addEventListener("click", this.boundCloseFilter);
    this.facetLink.addEventListener("keyup", this.boundHandleKeyup);
  }

  disconnectedCallback() {
    this.facetLink?.removeEventListener("click", this.boundCloseFilter);
    this.facetLink?.removeEventListener("keyup", this.boundHandleKeyup);
  }

  handleKeyup(event) {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
  }
}
customElements.define("facet-remove", FacetRemove);

class FacetApplyDrawer extends HTMLElement {
  constructor() {
    super();
    this.boundCloseFilter = this.closeFilter.bind(this);
    this.boundHandleKeypress = this.handleKeypress.bind(this);
    this.addEventListener("click", this.boundCloseFilter);
    this.addEventListener("keypress", this.boundHandleKeypress, false);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.boundCloseFilter);
    this.removeEventListener("keypress", this.boundHandleKeypress, false);
  }

  handleKeypress(event) {
    if (event.key === "Enter") {
      this.closeFilter(event);
    }
  }

  closeFilter(event) {
    event.preventDefault();
    const drawer = this.closest("facet-drawer");
    if (drawer) {
      eventModal(drawer, "close");
    }
  }
}
customElements.define("facet-apply-drawer", FacetApplyDrawer);

class SelectSorter extends HTMLElement {
  constructor() {
    super();
    this.boundActiveFilterSort = this.activeFilterSort.bind(this);
    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.listenersBound = false;
  }

  connectedCallback() {
    if (this.listenersBound) return;
    this.addEventListener("click", this.boundActiveFilterSort, false);
    document.addEventListener("click", this.boundHandleClickOutside, false);
    this.addEventListener("keydown", this.boundHandleKeydown, false);
    this.listenersBound = true;
  }

  disconnectedCallback() {
    if (!this.listenersBound) return;
    this.removeEventListener("click", this.boundActiveFilterSort, false);
    document.removeEventListener("click", this.boundHandleClickOutside, false);
    this.removeEventListener("keydown", this.boundHandleKeydown, false);
    this.listenersBound = false;
  }

  handleKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      this.activeFilterSort();
    } else if (event.key === "Escape") {
      event.preventDefault();
      this.closeDropdown();
    }
  }

  closeDropdown() {
    const selectCustom = this.closest(".select-custom");
    if (selectCustom && selectCustom.classList.contains("active")) {
      selectCustom.classList.remove("active");
      this.updateFilterSortTabindex(false);
    }
  }

  activeFilterSort() {
    const selectCustom = this.closest(".select-custom");
    if (!selectCustom) return;
    const isActive = selectCustom.classList.contains("active");
    selectCustom.classList.toggle("active");
    this.updateFilterSortTabindex(!isActive);
  }

  handleClickOutside(event) {
    if (!event.target.closest(".facets-vertical-form")) {
      document.querySelectorAll(".facets-vertical-form").forEach((element) => {
        const selectCustom = element.querySelector(".select-custom");
        if (!selectCustom) return;
        selectCustom.classList.remove("active");
        if (!selectCustom.classList.contains("active")) {
          this.updateFilterSortTabindex(false);
        }
      });
    }
  }

  updateFilterSortTabindex(isActive) {
    const filterSortElements = document.querySelectorAll("filter-sort");
    filterSortElements.forEach((element) => {
      if (isActive) {
        element.setAttribute("tabindex", "0");
      } else {
        element.removeAttribute("tabindex");
      }
    });
  }
}
customElements.define("select-sorter", SelectSorter);

class FilterSort extends FacetFiltersForm {
  constructor() {
    super();
    this.boundFilterSort = this.filterSort.bind(this);
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.focusTimer = null;
    this.addEventListener("click", this.boundFilterSort, false);
    this.addEventListener("keydown", this.boundHandleKeydown, false);
  }

  disconnectedCallback() {
    if (typeof super.disconnectedCallback === "function") {
      super.disconnectedCallback();
    }
    this.removeEventListener("click", this.boundFilterSort, false);
    this.removeEventListener("keydown", this.boundHandleKeydown, false);
    if (this.focusTimer) {
      clearTimeout(this.focusTimer);
      this.focusTimer = null;
    }
  }

  handleKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      this.filterSort(event);
    } else if (event.key === "Escape") {
      event.preventDefault();
      this.closeDropdownAndUpdateTabindex();
    }
  }

  filterSort(event) {
    event?.preventDefault();
    const select = document.querySelector("select.facet-filters__select");
    const value = this.getAttribute("value");
    if (select && value) {
      select.value = value;
      select.dispatchEvent(new Event("input", { bubbles: true }));
    }
    this.closeDropdownAndUpdateTabindex();
  }

  closeDropdownAndUpdateTabindex() {
    const facetFilters = this.closest(".facet-filters");
    if (facetFilters) {
      facetFilters.classList.remove("active");
    }

    this.updateFilterSortTabindex();
    this.focusSelectSorter();
  }

  updateFilterSortTabindex() {
    const filterSortElements = document.querySelectorAll("filter-sort");
    filterSortElements.forEach((element) => {
      element.removeAttribute("tabindex");
    });
  }

  focusSelectSorter() {
    if (this.focusTimer) clearTimeout(this.focusTimer);
    this.focusTimer = setTimeout(() => {
      this.focusTimer = null;
      if (!this.isConnected) return;
      const selectSorter =
        this.closest(".facet-filters")?.querySelector("select-sorter");
      if (selectSorter) {
        selectSorter.focus();
      }
    }, 100);
  }
}
customElements.define("filter-sort", FilterSort);

class FacetDrawer extends HTMLElement {
  constructor() {
    super();
    this.FacetsDrawer = document.getElementById("FacetsDrawer");
    this.boundOpenDrawer = this.openDrawer.bind(this);
    this.boundHandleResize = this.handleResize.bind(this);
    this.openTimer = null;
    this.lastWindowWidth = window.innerWidth;
    this.listenersBound = false;
  }

  connectedCallback() {
    if (this.listenersBound) return;
    this.FacetsDrawer = document.getElementById("FacetsDrawer");
    if (this.FacetsDrawer) {
      this.FacetsDrawer.addEventListener("click", this.boundOpenDrawer, false);
    }
    this.init();
    this.listenersBound = true;
  }

  disconnectedCallback() {
    if (!this.listenersBound) return;
    if (this.FacetsDrawer) {
      this.FacetsDrawer.removeEventListener("click", this.boundOpenDrawer, false);
    }
    window.removeEventListener("resize", this.boundHandleResize);
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
    this.listenersBound = false;
  }

  init() {
    const layout = this.dataset.layout;
    if (layout == "vertical") {
      window.addEventListener("resize", this.boundHandleResize);
    }
  }

  handleResize() {
    const newWidth = window.innerWidth;
    if (newWidth > 1024 && this.lastWindowWidth <= 1024 && this.isConnected) {
      eventModal(this, "close");
    }
    this.lastWindowWidth = newWidth;
  }

  openDrawer() {
    if (!this.FacetsDrawer) return;
    NextSkyTheme.getBody().appendChild(this);
    if (this.openTimer) clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => {
      this.openTimer = null;
      if (!this.isConnected) return;
      this.FacetsDrawer.classList.add("active");
      eventModal(this, "open", true, null, false, false);
      NextSkyTheme.global.rootToFocus = this.FacetsDrawer;
    }, 100);
  }
}
customElements.define("facet-drawer", FacetDrawer);

class FacetApplyCanvas extends FacetFiltersForm {
  constructor() {
    super();
    this.boundFacetApplyFilter = this.facetApplyFilter.bind(this);
    this.addEventListener("click", this.boundFacetApplyFilter, false);
  }

  disconnectedCallback() {
    if (typeof super.disconnectedCallback === "function") {
      super.disconnectedCallback();
    }
    this.removeEventListener("click", this.boundFacetApplyFilter, false);
  }
}
customElements.define("facet-apply-canvas", FacetApplyCanvas);

class ShowMoreButton extends HTMLButtonElement {
  constructor() {
    super();
    this.boundShowMoreFilter = this.ShowMoreFilter.bind(this);
    this.init();
  }

  init() {
    this.removeEventListener("click", this.boundShowMoreFilter);
    this.addEventListener("click", this.boundShowMoreFilter);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.boundShowMoreFilter);
  }

  ShowMoreFilter() {
    const button = this;
    const filterItemElements =
      this.closest(".filter-attribute").querySelectorAll(".hidden-load-more");
    if (button.classList.contains("show-more")) {
      button.classList.remove("show-more");
      button.classList.add("show-less");
      filterItemElements.forEach((element) => {
        element.classList.remove("hidden");
      });
    } else {
      button.classList.add("show-more");
      button.classList.remove("show-less");
      filterItemElements.forEach((element) => {
        element.classList.add("hidden");
      });
    }
  }
}
customElements.define("show-more-button", ShowMoreButton, {
  extends: "button",
});
CustomElement.observeAndPatchCustomElements({
  "show-more-button": {
    tagElement: "button",
    classElement: ShowMoreButton,
  },
});

class LoadMoreProduct extends HTMLElement {
  constructor() {
    super();
    this.boundFetchData = this.fetchData.bind(this);
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.abortController = null;
    this.stopInView = null;
    this.addEventListener("click", this.boundFetchData, false);
    this.addEventListener("keydown", this.boundHandleKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.boundFetchData, false);
    this.removeEventListener("keydown", this.boundHandleKeydown);
    this.abortFetch();
    if (typeof this.stopInView === "function") {
      this.stopInView();
      this.stopInView = null;
    }
  }

  handleKeydown(event) {
    if (event.key === "Enter") {
      this.fetchData(event);
    }
  }

  connectedCallback() {
    if (this.dataset.pagination == "infinity") {
      this.stopInView = Motion.inView(this, this.boundFetchData, {
        margin: "100px 0px -10px 0px",
      });
    }
  }

  abortFetch() {
    if (!this.abortController) return;
    this.abortController.abort();
    this.abortController = null;
  }

  fetchData() {
    const currentUrl = window.location.href;
    const param = window.location.search;
    let url = this.dataset.url;
    if (!url) return;
    if (param.length > 0) {
      url = currentUrl + url.replaceAll("?", "&");
    }
    this.abortFetch();
    const controller = new AbortController();
    this.abortController = controller;
    this.classList.add("loading");
    fetch(url, { signal: controller.signal })
      .then((response) => response.text())
      .then((html) => {
        if (!this.isConnected) return;
        const htmlRender = new DOMParser().parseFromString(html, "text/html");
        const productGrid = getProductGrid();
        const nextPagination = htmlRender.querySelector(".pagination-load-more");
        const currentPagination = document.querySelector(".pagination-load-more");
        const resultNodesHtml = htmlRender.querySelectorAll(
          "#product-grid .product-item"
        );
        if (!productGrid) return;

        resultNodesHtml.forEach((prodNode) => productGrid.appendChild(prodNode));
        if (currentPagination && nextPagination) {
          currentPagination.innerHTML = nextPagination.innerHTML;
        }
        new LazyLoader(".image-lazy-load");
        getCollectionGrid()
          ?.querySelector("motion-items-effect")
          ?.reloadAnimationEffect();
      })
      .finally(() => {
        if (this.abortController === controller) {
          this.abortController = null;
        }
        this.classList.remove("loading");
      })
      .catch((e) => {
        if (e.name === "AbortError") return;
        console.error(e);
      });
  }
}
if (!customElements.get("loadmore-button")) {
  customElements.define("loadmore-button", LoadMoreProduct);
}

class StickyToolbar extends HTMLElement {
  constructor() {
    super();
    this.isSticky = false;
    this.toolbarHeight = 0;
    this.triggerPosition = 0;
    this.ticking = false;
    this.initFrame = 0;
    this.scrollFrame = 0;
    this.timeoutIds = [];
    this.boundHandleScroll = this.handleScroll.bind(this);
    this.boundHandleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    this.init();
    this.bindEvents();
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.boundHandleScroll);
    window.removeEventListener("resize", this.boundHandleResize);
    this.deactivateSticky();
    NextSkyTheme.getBody().style.removeProperty("--sticky-toolbar-height");
    if (this.scrollFrame) {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = 0;
    }
    if (this.initFrame) {
      cancelAnimationFrame(this.initFrame);
      this.initFrame = 0;
    }
    this.timeoutIds.forEach((id) => clearTimeout(id));
    this.timeoutIds = [];
    this.ticking = false;
  }

  init() {
    if (window.innerWidth >= 1024.98) {
      return;
    }

    if (this.initFrame) cancelAnimationFrame(this.initFrame);
    this.initFrame = requestAnimationFrame(() => {
      this.initFrame = 0;
      if (!this.isConnected) return;
      this.toolbarHeight = this.offsetHeight;

      this.calculateTriggerPosition();

      NextSkyTheme.getBody().style.setProperty(
        "--sticky-toolbar-height",
        this.toolbarHeight + "px"
      );

      this.checkInitialStickyState();
    });
  }

  checkInitialStickyState() {
    const attemptCheck = (delay = 0, maxAttempts = 5, currentAttempt = 1) => {
      const timeoutId = setTimeout(() => {
        this.timeoutIds = this.timeoutIds.filter((id) => id !== timeoutId);
        if (!this.isConnected) return;

        this.calculateTriggerPosition();

        const currentScrollY = window.scrollY;
        if (currentScrollY > this.triggerPosition && !this.isSticky) {
          this.activateSticky();
        } else if (currentAttempt < maxAttempts) {
          attemptCheck(delay * 2 || 50, maxAttempts, currentAttempt + 1);
        }
      }, delay);
      this.timeoutIds.push(timeoutId);
    };

    attemptCheck();
  }

  calculateTriggerPosition() {
    const allSections = document.querySelectorAll(".shopify-section");
    let totalHeight = 0;

    let toolbarSection = null;
    for (const section of allSections) {
      if (section.contains(this)) {
        toolbarSection = section;
        break;
      }
    }

    for (const section of allSections) {
      if (section === toolbarSection) {
        if (section.classList.contains("shopify-section-search")) {
          const titleWrapper = section.querySelector(
            ".title-wrapper-with-link"
          );
          if (titleWrapper) {
            totalHeight += titleWrapper.offsetHeight;
          }
        }
        const collectionSteps = section.querySelector(".collection-steps");
        if (collectionSteps) {
          totalHeight += collectionSteps.offsetHeight;
        }
        break;
      }

      const position = section.compareDocumentPosition(this);

      if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
        if (section.classList.contains("shopify-section-search")) {
          const titleWrapper = section.querySelector(
            ".title-wrapper-with-link"
          );
          if (titleWrapper) {
            totalHeight += titleWrapper.offsetHeight;
          }
        } else {
          totalHeight += section.offsetHeight;
        }
        const collectionSteps = section.querySelector(".collection-steps");
        if (collectionSteps) {
          totalHeight += collectionSteps.offsetHeight;
        }
      }
    }

    this.triggerPosition = totalHeight;
  }

  bindEvents() {
    window.removeEventListener("scroll", this.boundHandleScroll);
    window.removeEventListener("resize", this.boundHandleResize);
    window.addEventListener("scroll", this.boundHandleScroll, { passive: true });
    window.addEventListener("resize", this.boundHandleResize, { passive: true });
  }

  handleScroll() {
    if (this.ticking) return;
    this.ticking = true;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = 0;
      if (!this.isConnected) {
        this.ticking = false;
        return;
      }
      this.onScroll();
      this.ticking = false;
    });
  }

  handleResize() {
    this.init();
  }

  onScroll() {
    if (!this.isConnected) return;

    if (window.innerWidth >= 1024.98) {
      if (this.isSticky) {
        this.deactivateSticky();
      }
      return;
    }

    const currentScrollY = window.scrollY;
    const shouldBeSticky = currentScrollY > this.triggerPosition;

    if (shouldBeSticky && !this.isSticky) {
      this.activateSticky();
    } else if (!shouldBeSticky && this.isSticky) {
      this.deactivateSticky();
    }
  }

  activateSticky() {
    this.isSticky = true;
    NextSkyTheme.getBody().classList.add("has-sticky-toolbar");
    NextSkyTheme.getBody().style.setProperty(
      "--sticky-toolbar-height",
      this.toolbarHeight + "px"
    );
  }

  deactivateSticky() {
    this.isSticky = false;
    NextSkyTheme.getBody().classList.remove("has-sticky-toolbar");
  }
}

customElements.define("sticky-toolbar", StickyToolbar);

class CollapsibleHorizontalRow extends HTMLDetailsElement {
  constructor() {
    super();

    this.summaryElement = null;
    this.contentElement = null;
    this._open = false;

    this.detectClickOutsideListener = this.detectClickOutside.bind(this);
    this.detectEscKeyboardListener = this.detectEscKeyboard.bind(this);
    this.boundHandleToggle = this.handleToggle.bind(this);
    this.boundHandleSummaryClick = this.handleSummaryClick.bind(this);
    this.animationControls = new Set();
  }

  connectedCallback() {
    this.removeEventListener("toggle", this.boundHandleToggle);
    this.addEventListener("toggle", this.boundHandleToggle);

    this.init();
  }

  disconnectedCallback() {
    this.removeEventListener("toggle", this.boundHandleToggle);
    this.summaryElement?.removeEventListener(
      "click",
      this.boundHandleSummaryClick
    );
    document.removeEventListener("click", this.detectClickOutsideListener);
    document.removeEventListener("keydown", this.detectEscKeyboardListener);
    this.cancelAnimations();
  }

  handleToggle(event) {
    event.stopImmediatePropagation();
  }

  handleSummaryClick(event) {
    event.preventDefault();
    this.open = !this.open;
  }

  set open(value) {
    const newValue = Boolean(value);
    if (newValue === this._open) return;

    this._open = newValue;

    if (!this.isConnected) {
      newValue ? this.setAttribute("open", "") : this.removeAttribute("open");
      return;
    }

    this.transition(newValue);
  }

  get open() {
    return this._open;
  }

  init() {
    this.summaryElement = this.querySelector("summary");
    this.contentElement = this.querySelector(".collapsible-row__content");
    this.removeAttribute('open');
    if (!this.summaryElement || !this.contentElement) return;

    this._open = this.hasAttribute("open");
    this.summaryElement.removeEventListener(
      "click",
      this.boundHandleSummaryClick
    );
    this.summaryElement.addEventListener("click", this.boundHandleSummaryClick);
  }

  async transition(isOpen) {
    this.cancelAnimations();

    if (isOpen) {
      this.setAttribute("open", "");
      this.summaryElement.setAttribute("open", "");
      this.classList.add("detail-open");

      document.addEventListener("click", this.detectClickOutsideListener);
      document.addEventListener("keydown", this.detectEscKeyboardListener);

      await this.fadeInDown();
      if (!this.isConnected || !this._open) return;
      this.contentElement.setAttribute("open", "");
    } else {
      this.summaryElement.removeAttribute("open");
      this.contentElement.removeAttribute("open");
      this.classList.remove("detail-open");

      document.removeEventListener("click", this.detectClickOutsideListener);
      document.removeEventListener("keydown", this.detectEscKeyboardListener);

      await this.fadeInUp();
      if (!this.isConnected || this._open) return;
      if (!this._open) this.removeAttribute("open");
    }
  }

  detectClickOutside(event) {
    if (!this.open) return;
    if (this.contains(event.target)) return;

    this.open = false;
  }

  detectEscKeyboard(event) {
    if (event.key === "Escape") {
      this.open = false;
      this.summaryElement.focus({ focusVisible: true });
    }
  }

  fadeInDown() {
    const animation = this.trackAnimation(Motion.animate(
      this.contentElement,
      { opacity: [0, 1] },
      {
        duration: 0.2,
        easing: [0.22, 0.61, 0.36, 1],
      }
    ));
    return animation.finished?.catch(() => {});
  }

  fadeInUp() {
    const animation = this.trackAnimation(Motion.animate(
      this.contentElement,
      { opacity: [1, 0] },
      { duration: 0.2 }
    ));
    return animation.finished?.catch(() => {});
  }

  trackAnimation(animation) {
    if (!animation) return { finished: Promise.resolve() };
    this.animationControls.add(animation);
    animation.finished
      ?.catch(() => {})
      .finally(() => {
        this.animationControls.delete(animation);
      });
    return animation;
  }

  cancelAnimations() {
    this.animationControls.forEach((animation) => {
      if (typeof animation.cancel === "function") {
        animation.cancel();
      }
    });
    this.animationControls.clear();
  }
}

customElements.define("collapsible-horizontal-row", CollapsibleHorizontalRow, {
  extends: "details",
});

CustomElement.observeAndPatchCustomElements({
  "collapsible-horizontal-row": {
    tagElement: "details",
    classElement: CollapsibleHorizontalRow,
  },
});
